import { useCallback, useEffect, useRef, useState } from 'react';
import { compressToUTF16, decompressFromUTF16 } from 'lz-string';
const DEFAULT_HISTORY_LIMIT = 50;
const DEFAULT_DEBOUNCE_DELAY = 600;
const MAX_STORAGE_SIZE_BYTES = 500 * 1024;
const estimateSize = (data) => {
    try {
        const serialized = JSON.stringify(data);
        return serialized.length * 2;
    }
    catch {
        return 0;
    }
};
const trimHistoryBySize = (history, maxSizeBytes) => {
    if (history.undoStack.length <= 2 && history.redoStack.length <= 2) {
        return history;
    }
    let currentHistory = { ...history };
    let currentSize = estimateSize(currentHistory);
    if (currentSize <= maxSizeBytes) {
        return currentHistory;
    }
    while (currentSize > maxSizeBytes && currentHistory.undoStack.length > 1) {
        const removed = currentHistory.undoStack[0];
        currentHistory = {
            ...currentHistory,
            undoStack: currentHistory.undoStack.slice(1)
        };
        const removedSize = estimateSize(removed);
        currentSize -= removedSize;
        if (currentHistory.undoStack.length % 5 === 0) {
            currentSize = estimateSize(currentHistory);
        }
    }
    while (currentSize > maxSizeBytes && currentHistory.redoStack.length > 0) {
        const removed = currentHistory.redoStack[currentHistory.redoStack.length - 1];
        currentHistory = {
            ...currentHistory,
            redoStack: currentHistory.redoStack.slice(0, -1)
        };
        const removedSize = estimateSize(removed);
        currentSize -= removedSize;
        if (currentHistory.redoStack.length % 5 === 0) {
            currentSize = estimateSize(currentHistory);
        }
    }
    currentSize = estimateSize(currentHistory);
    if (currentSize > maxSizeBytes) {
        console.warn('Single history item exceeds size limit. Clearing history.');
        return null;
    }
    return currentHistory;
};
const createEmptyHistory = (initialValue) => ({
    undoStack: [initialValue],
    redoStack: [],
});
export const useOperationHistory = (sessionKey, initialValue, historyLimit, debounceDelay) => {
    const limit = historyLimit || DEFAULT_HISTORY_LIMIT;
    const delay = debounceDelay || DEFAULT_DEBOUNCE_DELAY;
    const [history, setHistory] = useState(() => {
        try {
            const raw = sessionStorage.getItem(sessionKey);
            if (!raw) {
                return {
                    undoStack: [initialValue],
                    redoStack: [],
                };
            }
            const decompressed = decompressFromUTF16(raw);
            if (!decompressed)
                throw new Error('Failed to decompress');
            const parsed = JSON.parse(decompressed);
            if (!Array.isArray(parsed.undoStack))
                throw new Error('Invalid structure');
            return trimHistoryBySize(parsed, MAX_STORAGE_SIZE_BYTES) || createEmptyHistory(initialValue);
        }
        catch {
            return {
                undoStack: [initialValue],
                redoStack: [],
            };
        }
    });
    const current = history?.undoStack?.[history.undoStack?.length - 1];
    useEffect(() => {
        setHistory(prev => {
            if (prev.undoStack.length > 0 && JSON.stringify(prev.undoStack[0]) !== JSON.stringify(initialValue)) {
                return {
                    ...prev,
                    undoStack: [initialValue, ...prev.undoStack.slice(1)]
                };
            }
            return prev;
        });
    }, [initialValue]);
    const saveTimeoutRef = useRef(null);
    const lastSavedHistoryRef = useRef('');
    useEffect(() => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = setTimeout(() => {
            try {
                const historyString = JSON.stringify(history);
                if (lastSavedHistoryRef.current === historyString) {
                    return;
                }
                const compressed = compressToUTF16(historyString);
                sessionStorage.setItem(sessionKey, compressed);
                lastSavedHistoryRef.current = historyString;
            }
            catch (err) {
                if (err instanceof Error && err.name === 'QuotaExceededError') {
                    console.warn('Session storage quota exceeded. Clearing operation history.');
                    const emptyHistory = createEmptyHistory(history.undoStack[history.undoStack.length - 1]);
                    try {
                        const emptyHistoryString = JSON.stringify(emptyHistory);
                        const emptyCompressed = compressToUTF16(emptyHistoryString);
                        sessionStorage.setItem(sessionKey, emptyCompressed);
                        lastSavedHistoryRef.current = emptyHistoryString;
                        setHistory(emptyHistory);
                    }
                    catch (retryErr) {
                        console.error('Failed to save even empty history. Removing session storage entry.');
                        sessionStorage.removeItem(sessionKey);
                        lastSavedHistoryRef.current = '';
                    }
                }
                else {
                    console.error('Failed to store operation history:', err);
                }
            }
        }, delay);
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
                saveTimeoutRef.current = null;
            }
        };
    }, [history, sessionKey, delay]);
    const push = useCallback((entry) => {
        setHistory(prev => {
            const last = prev.undoStack[prev.undoStack.length - 1];
            if (last === entry)
                return prev;
            try {
                if (JSON.stringify(last) === JSON.stringify(entry))
                    return prev;
            }
            catch {
            }
            const newUndoStack = [...prev.undoStack, entry].slice(-limit);
            let newHistory = {
                undoStack: newUndoStack,
                redoStack: [],
            };
            const trimmedHistory = trimHistoryBySize(newHistory, MAX_STORAGE_SIZE_BYTES);
            return trimmedHistory || createEmptyHistory(entry);
        });
    }, [limit]);
    const undo = useCallback(() => {
        let newCurrentValue = null;
        setHistory(prev => {
            if (prev.undoStack.length <= 1)
                return prev;
            const newUndoStack = [...prev.undoStack];
            const popped = newUndoStack.pop();
            const newHistory = {
                undoStack: newUndoStack,
                redoStack: [popped, ...prev.redoStack],
            };
            newCurrentValue = newHistory.undoStack[newHistory.undoStack.length - 1];
            return newHistory;
        });
        return newCurrentValue;
    }, []);
    const redo = useCallback(() => {
        let newCurrentValue = null;
        setHistory(prev => {
            if (prev.redoStack.length === 0)
                return prev;
            const [next, ...rest] = prev.redoStack;
            let newHistory = {
                undoStack: [...prev.undoStack, next].slice(-limit),
                redoStack: rest,
            };
            const trimmedHistory = trimHistoryBySize(newHistory, MAX_STORAGE_SIZE_BYTES);
            const finalHistory = trimmedHistory || createEmptyHistory(next);
            newCurrentValue = finalHistory.undoStack[finalHistory.undoStack.length - 1];
            return finalHistory;
        });
        return newCurrentValue;
    }, [limit]);
    return {
        current,
        push,
        undo,
        redo,
        canUndo: (history?.undoStack?.length ?? 0) > 1,
        canRedo: (history?.redoStack?.length ?? 0) > 0,
    };
};
